import Modal from '../common/Modal';
import Spinner from '../common/Spinner';
import { useDedicatedIpPurchase, useDedicatedIpPrice } from '../../hooks/useBilling';
import type { DedicatedIpPrice } from '../../api/billing';
import { formatDateShort } from '../../utils/date';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  hostname: string;
  subscriptionId: string;
}

function formatAmount(amount: number, currency: string): string | null {
  // A malformed currency code from the API must degrade to the price-less
  // wording, not throw out of render (there is no ErrorBoundary above this modal).
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(amount);
  } catch {
    return null;
  }
}

function formatPrice({ amount, currency, interval }: DedicatedIpPrice): string | null {
  const formatted = formatAmount(amount, currency);
  return formatted ? `${formatted}/${interval === 'year' ? 'year' : 'month'}` : null;
}

export default function DedicatedIpModal({ isOpen, onClose, hostname, subscriptionId }: Props) {
  const purchase = useDedicatedIpPurchase();
  const price = useDedicatedIpPrice(subscriptionId, isOpen);
  const priceLabel = price.data ? formatPrice(price.data) : null;
  // Credit period: the amount charged on Confirm and when the credit period ends.
  const dueToday = price.data?.due_today;
  const dueTodayLabel =
    price.data && dueToday !== undefined ? formatAmount(dueToday, price.data.currency) : null;
  const creditPeriodEnd = price.data?.credit_period_end;

  // Confirm only an amount the customer was actually shown.
  const shownDueToday = dueTodayLabel && creditPeriodEnd ? dueToday : undefined;

  const handleConfirm = () => {
    purchase.mutate(
      { subscriptionId, confirmDueToday: shownDueToday },
      {
        onSuccess: () => {
          onClose();
        },
        // A refusal may mean the amount due today changed: show the current one.
        onError: () => {
          void price.refetch();
        },
      }
    );
  };

  const handleClose = () => {
    if (!purchase.isPending) {
      purchase.reset();
      onClose();
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Add Dedicated IP">
      <div className="space-y-3">
        <p className="text-sm text-gray-700">
          Add a dedicated IP address to <span className="font-medium">{hostname}</span>.
        </p>

        <ul className="text-sm text-gray-600 space-y-2">
          {dueTodayLabel && creditPeriodEnd ? (
            <li>
              {`Your subscription is in its credit period until ${formatDateShort(creditPeriodEnd)}. `}
              {'Adding a dedicated IP charges '}
              <span className="font-medium text-gray-900">{dueTodayLabel}</span>
              {' today to cover it until then'}
              {priceLabel ? (
                <>
                  {', then '}
                  <span className="font-medium text-gray-900">{priceLabel}</span>
                  {' with your plan.'}
                </>
              ) : (
                '.'
              )}
            </li>
          ) : (
            <li>
              A dedicated IP will be added to your existing subscription
              {priceLabel ? (
                <>
                  {' for '}
                  <span className="font-medium text-gray-900">{priceLabel}</span>
                  {', '}
                </>
              ) : (
                ' and '
              )}
              prorated to your billing cycle.
            </li>
          )}
          <li>Activation typically takes 15-30 minutes.</li>
          <li>Once active, you can use DHCP Option 43 to point devices directly to your controller's IP address.</li>
        </ul>

        {purchase.isError && (
          <div className="bg-red-50 border border-red-200 rounded-md p-3">
            <p className="text-sm text-red-700">
              {purchase.error instanceof Error ? purchase.error.message : 'Failed to add dedicated IP'}
            </p>
          </div>
        )}

        <div className="flex gap-3 pt-2">
          <button
            onClick={handleConfirm}
            disabled={purchase.isPending}
            className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 font-medium transition-colors flex items-center justify-center gap-2"
          >
            {purchase.isPending ? (
              <>
                <Spinner size="sm" className="border-white border-t-transparent" />
                <span>Adding...</span>
              </>
            ) : (
              'Add Dedicated IP'
            )}
          </button>
          <button
            onClick={handleClose}
            disabled={purchase.isPending}
            className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 disabled:opacity-50 font-medium transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </Modal>
  );
}
